import React from 'react';

import Stack from '@mui/material/Stack';
import ButtonBase from '@mui/material/ButtonBase';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import SegmentAnalytics from 'ff/common/SegmentAnalytics';

import {
    useCurrentTracklog,
    useCurrentDebrief,
    useChrono,
    useDebriefApp,
    useMapControls,
    useDebriefSegments,
} from 'ff/debrief/hooks';
import { Segment } from 'ff/debrief/models';
import { LinkToTracklog } from 'ff/debrief/router';

import SidebarPanel from 'ff/debrief/layouts/SidebarPanel';
import FlightInfo from 'ff/debrief/components/segments/FlightInfo';
import OverallScoreBar from 'ff/debrief/components/segments/OverallScoreBar';
import SegmentList from 'ff/debrief/components/segments/list/SegmentList';
import SectionHeader from 'ff/debrief/components/segments/SectionHeader';
import TopLevelScores from 'ff/debrief/components/segments/TopLevelScores';

const Segments = () => {
    const {
        isShareApp,
        selectedSegment,
        selectSegment,
        isHidingSegmentDetails,
    } = useDebriefApp();
    const { tracklog } = useCurrentTracklog();
    const { debrief } = useCurrentDebrief();
    const { segments } = useDebriefSegments();
    const { flyToPoints } = useMapControls();
    const { setOffset, resetChrono, pause } = useChrono();

    React.useEffect(() => {
        resetChrono();
    }, []);

    const handleSegmentClick = (segment: Segment) => {
        SegmentAnalytics.logEvent('Debrief, Segment Clicked', {
            segmentName: segment.label,
            segmentScore: segment?.score || null,
        });
        pause();
        setOffset(segment.startOffsetInSeconds);
        selectSegment(segment);
        const segmentPoints = segments.find(s => s.id === segment.id).points;
        const shouldAddExtraPadding =
            segment?.scoringDetails !== undefined &&
            segment?.scoringDetails?.length > 0 &&
            !isHidingSegmentDetails;
        flyToPoints(segmentPoints, shouldAddExtraPadding);
    };

    const handleBackButtonClick = () => {
        selectSegment(null);
        resetChrono();
    };

    return (
        <SidebarPanel>
            <Stack py={2} px={0.5} height="100%">
                <FlightInfo
                    {...tracklog}
                    action={
                        !isShareApp && (
                            <ButtonBase
                                component={LinkToTracklog}
                                onClick={handleBackButtonClick}
                                sx={{ padding: 1 }}
                            >
                                <ArrowBackIosIcon />
                            </ButtonBase>
                        )
                    }
                />

                <OverallScoreBar score={debrief?.score} sx={{ mb: 3 }} />

                <TopLevelScores scores={debrief?.topLevelScores} />

                <SectionHeader>Segments</SectionHeader>
                <SegmentList
                    segments={debrief?.debriefSegments}
                    currentSegmentId={selectedSegment?.id}
                    onSegmentClick={handleSegmentClick}
                />
            </Stack>
        </SidebarPanel>
    );
};

export default Segments;
